import { supabase } from '../lib/supabase';

/**
 * Returns a Date object representing the start of the given period.
 * period: '7d' | '30d' | '1y' | 'all'
 */
function getPeriodStart(period) {
  if (period === 'all') return null;
  const now = new Date();
  if (period === '7d') {
    now?.setDate(now?.getDate() - 7);
  } else if (period === '30d') {
    now?.setDate(now?.getDate() - 30);
  } else if (period === '1y') {
    now?.setFullYear(now?.getFullYear() - 1);
  }
  return now?.toISOString();
}

export const communityStatsService = {
  async getStats(period = 'all') {
    const since = getPeriodStart(period);

    // Helper to apply date filter
    const withDate = (query) => {
      if (since) return query?.gte('created_at', since);
      return query;
    };

    // 1. Total active offers
    let activeOffersQuery = supabase?.from('marketplace_wishes')?.select('id', { count: 'exact', head: true })?.eq('offer_status', 'active');
    if (since) activeOffersQuery = activeOffersQuery?.gte('created_at', since);
    const { count: activeOffers, error: e1 } = await activeOffersQuery;

    // 2. Total completed deals (negotiations with status='completed')
    let completedDealsQuery = supabase?.from('negotiations')?.select('id', { count: 'exact', head: true })?.eq('status', 'completed');
    if (since) completedDealsQuery = completedDealsQuery?.gte('created_at', since);
    const { count: completedDeals, error: e2 } = await completedDealsQuery;

    // 3. Total archived/expired offers
    let archivedOffersQuery = supabase?.from('marketplace_wishes')?.select('id', { count: 'exact', head: true })?.in('offer_status', ['archived', 'expired']);
    if (since) archivedOffersQuery = archivedOffersQuery?.gte('created_at', since);
    const { count: archivedOffers, error: e3 } = await archivedOffersQuery;

    // 4. Active users (users who posted at least one offer in the period)
    let activeUsersQuery = supabase?.from('marketplace_wishes')?.select('user_id');
    if (since) activeUsersQuery = activeUsersQuery?.gte('created_at', since);
    const { data: activeUsersData, error: e4 } = await activeUsersQuery;
    const activeUsers = new Set((activeUsersData || []).map(r => r.user_id))?.size;

    // 5. Disputes opened
    let disputesOpenedQuery = supabase?.from('disputes')?.select('id', { count: 'exact', head: true });
    if (since) disputesOpenedQuery = disputesOpenedQuery?.gte('created_at', since);
    const { count: disputesOpened, error: e5 } = await disputesOpenedQuery;

    // 6. Disputes resolved
    let disputesResolvedQuery = supabase?.from('disputes')?.select('id', { count: 'exact', head: true })?.in('status', ['resolved', 'closed']);
    if (since) disputesResolvedQuery = disputesResolvedQuery?.gte('created_at', since);
    const { count: disputesResolved, error: e6 } = await disputesResolvedQuery;

    // 7. Category breakdown from marketplace_wishes
    let categoryQuery = supabase?.from('marketplace_wishes')?.select('category');
    if (since) categoryQuery = categoryQuery?.gte('created_at', since);
    const { data: categoryData, error: e7 } = await categoryQuery;

    const categoryMap = {};
    (categoryData || [])?.forEach(r => {
      const cat = r?.category || 'Ostalo';
      categoryMap[cat] = (categoryMap?.[cat] || 0) + 1;
    });
    const topCategories = Object.entries(categoryMap)?.sort((a, b) => b?.[1] - a?.[1])?.slice(0, 8)?.map(([name, count]) => ({ name, count }));

    // 8. Top vouchers by transaction count (from negotiations)
    let topVouchersQuery = supabase?.from('negotiations')?.select('voucher_title');
    if (since) topVouchersQuery = topVouchersQuery?.gte('created_at', since);
    const { data: voucherData, error: e8 } = await topVouchersQuery;

    const voucherMap = {};
    (voucherData || [])?.forEach(r => {
      const name = r?.voucher_title || 'Nepoznat vaučer';
      voucherMap[name] = (voucherMap?.[name] || 0) + 1;
    });
    const topVouchers = Object.entries(voucherMap)?.sort((a, b) => b?.[1] - a?.[1])?.slice(0, 8)?.map(([name, count]) => ({ name, count }));

    return {
      activeOffers: activeOffers || 0,
      completedDeals: completedDeals || 0,
      archivedOffers: archivedOffers || 0,
      activeUsers,
      disputesOpened: disputesOpened || 0,
      disputesResolved: disputesResolved || 0,
      topCategories,
      topVouchers,
    };
  }
};
